'use client';

import { useEffect, useState } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { createClient } from '@/lib/supabase/client';

export default function PaymentSuccessPage() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Confirming your payment...');
  const supabase = createClient();

  useEffect(() => {
    const paymentIntent = searchParams.get('payment_intent');
    const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');

    if (!paymentIntent || !paymentIntentClientSecret) {
      setStatus('error');
      setMessage('Invalid payment confirmation');
      return;
    }

    // Check payment status
    async function checkPayment() {
      try {
        // Wait a moment for webhook to process
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Check if payment was recorded
        const { data: payment } = await supabase
          .from('payments')
          .select('status, match_id')
          .eq('stripe_payment_id', paymentIntent)
          .single();

        if (payment?.status === 'charged') {
          setStatus('success');
          setMessage('Your payment was successful and the worker has been notified!');
        } else {
          // Payment might still be processing via webhook
          setStatus('success');
          setMessage('Payment received! Processing your hire...');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        setStatus('success'); // Assume success since Stripe redirected here
        setMessage('Payment received! Processing your hire...');
      }
    }

    checkPayment();
  }, [searchParams, supabase]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="mx-auto h-16 w-16 animate-spin text-primary" />
            <h1 className="mt-6 text-2xl font-bold">Confirming Payment</h1>
            <p className="mt-2 text-muted-foreground">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="mt-6 text-2xl font-bold">Payment Successful!</h1>
            <p className="mt-2 text-muted-foreground">{message}</p>
            <div className="mt-8 space-y-3">
              <Button asChild className="w-full">
                <Link href="/contractor/matches">Back to Matches</Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link href="/dashboard">Go to Dashboard</Link>
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-10 w-10 text-destructive" />
            </div>
            <h1 className="mt-6 text-2xl font-bold">Payment Error</h1>
            <p className="mt-2 text-muted-foreground">{message}</p>
            <div className="mt-8 space-y-3">
              <Button asChild variant="outline" className="w-full">
                <Link href="/contractor/matches">Back to Matches</Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
